import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import TechStackPreview from 'components/chip/TechStackPreview';
import TechStackSelect from 'components/tech-stack-select/TechStackSelect';
import { TechStackResult, getTechStack } from 'stores/ProjectStoreAdapter';
import { errorDefault } from 'utils/Toastify';

// --- 型定義 ---

type TechStackFormFieldProps = {
  /** グループ名 */
  groupName: string;
  /** 表示モード: edit=フォーム入力, preview=読み取り専用 */
  mode: 'edit' | 'preview';
  /** 選択中の技術スタックIDリスト */
  selectedIds: string[];
  /** 値変更ハンドラ（editモード時） */
  onChange: (ids: string[]) => void;
  /** アイテムが無効（disabled）かどうか */
  disabled?: boolean;
  /** ラベル部分の最小幅 (デフォルト: 140px) */
  labelMinWidth?: number;
  /** アコーディオンのデフォルト展開状態 (デフォルト: true) */
  defaultExpanded?: boolean;
};

/**
 * 要員スキル情報（固定項目グループ）用フォームフィールド。
 *
 * 固定項目 `FIXED_MEMBER_SKILL` のテンプレートグループに対応し、
 * 技術スタック取得APIを利用した `TechStackSelect` を入力コンポーネントとして使用する。
 *
 * ビューは `TemplateGroupView` と同じアコーディオン形式で表示する。
 */
const TechStackFormField: React.FC<TechStackFormFieldProps> = ({
  groupName,
  mode,
  selectedIds,
  onChange,
  disabled = false,
  labelMinWidth = 140,
  defaultExpanded = true,
}) => {
  const [techStackData, setTechStackData] = useState<TechStackResult | null>(null);

  useEffect(() => {
    getTechStack()
      .then((data) => setTechStackData(data))
      .catch(() => errorDefault('技術スタックの取得に失敗しました'));
  }, []);

  /** 選択中IDからカテゴリ種別ごとの技術スタックを生成 */
  const getLayerGroups = (): { group: string; names: string[] }[] => {
    if (!techStackData || selectedIds.length === 0) return [];
    const uniqueIds = new Set(selectedIds);
    const grouped: Record<string, string[]> = {};
    (techStackData.TECH_LAYER || []).forEach((item) => {
      if (uniqueIds.has(item.techStackId)) {
        const key = item.categoryLabel;
        if (!grouped[key]) grouped[key] = [];
        if (!grouped[key].includes(item.techStackName)) {
          grouped[key].push(item.techStackName);
        }
      }
    });
    return Object.entries(grouped).map(([group, names]) => ({ group, names }));
  };

  const getTypeGroups = (): { group: string; names: string[] }[] => {
    if (!techStackData || selectedIds.length === 0) return [];
    const uniqueIds = new Set(selectedIds);
    const grouped: Record<string, string[]> = {};
    (techStackData.TECH_TYPE || []).forEach((item) => {
      if (uniqueIds.has(item.techStackId)) {
        const key = item.categoryLabel;
        if (!grouped[key]) grouped[key] = [];
        if (!grouped[key].includes(item.techStackName)) {
          grouped[key].push(item.techStackName);
        }
      }
    });
    return Object.entries(grouped).map(([group, names]) => ({ group, names }));
  };

  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => setExpanded((prev) => !prev);

  return (
    <Stack spacing={1}>
      <Accordion
        elevation={0}
        expanded={expanded}
        sx={{
          '&:before': { display: 'none' },
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton size="small" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
              <ExpandMoreIcon />
            </IconButton>
          }
          sx={{
            px: 2,
            minHeight: 40,
            flexDirection: 'row-reverse',
            gap: 1,
            cursor: 'default',
            '&.Mui-focusVisible': { bgcolor: 'transparent' },
            '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'none' },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            onClick={handleToggle}
            sx={{ cursor: 'pointer' }}
          >
            {groupName}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minWidth: labelMinWidth,
                maxWidth: labelMinWidth,
                pt: 1,
                wordBreak: 'break-word',
                flexShrink: 0,
              }}
            >
              技術スタック
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {mode === 'edit' ? (
                <TechStackSelect
                  techStackData={techStackData}
                  selectedIds={selectedIds}
                  onChange={onChange}
                  disabled={disabled}
                />
              ) : (
                <TechStackPreview layerGroups={getLayerGroups()} typeGroups={getTypeGroups()} />
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default TechStackFormField;
